import { useState } from "react";
import { Trans } from "react-i18next";

import { getFeatureFlags } from "@/lib/runtime-config";
import type { CollectorsDiscountData } from "@/hooks/use-apply-payment";
import {
  DRYER_STEPS,
  dryerDoorClosed,
  guideFailed,
  guideView,
  sendsStartSignal,
  showsStartTimeout,
  showsStepDots,
  stepsWithTexts,
  type DryerView,
  type GuideTexts,
} from "@frontend-common/start-guide/start-guide-logic";
import { DryerScene } from "@/features/wash-start-guide/dryer-scene";
import {
  GuideKeyframes,
  guideHeader,
  guideStatus,
  GuideSteps,
  type GuidePhase,
  type GuideStep,
  type InvoiceOffer,
  RunningState,
  type StepTexts,
} from "@/features/wash-start-guide/start-guide-parts";
import { FailedState } from "@/features/wash-start-guide/start-failed-state";
import { FlowBackSlot, FlowHeader } from "@/features/payment-flow/flow-header";

// The dryer version of the start guide.
// The dryer starts as soon as it has the moxa impulse AND a closed door, and
// the temperature can't be changed once it runs. So payment alone doesn't send
// the impulse: we wait until the user confirms the temperature
// (`onTemperatureSet`), and only then does the backend pulse the moxa. Closing
// the door (or having it closed already) then starts the machine, and
// `isRunning` jumps to the success view from any step.
//
// Alternative order (`phase`), used by the payment flow: "prepare" runs the
// door and temperature steps BEFORE payment and ends with `onPrepared`; after
// payment, "after-prepare" only waits for the machine to start (the impulse
// goes out with the payment).

export type DryStartGuidePhase = GuidePhase;

export type DryStartGuideScreenProps = {
  machineNumber: number;
  phase?: DryStartGuidePhase;
  // "prepare" only: the user set the temperature and closed the door
  onPrepared?: () => void;
  // "prepare" only: back from the first step, to the payment method selection
  onBack?: () => void;
  // kg, from the machine's `capacity` field; drawn on the dryer's sticker
  machineCapacity: number;
  isRunning: boolean;
  // the user confirmed the temperature: time to send the start impulse
  onTemperatureSet?: () => void;
  isNewRegistration?: boolean;
  // offer an invoice on the success view; `result` when back from the form
  invoice?: InvoiceOffer;
  collectorsDiscount?: CollectorsDiscountData;
  currencySymbol?: string;
  returnAfterSeconds?: number;
  onStartAnother?: () => void;
  onFinish?: () => void;
  // payment session id (the receipt's "Blokk sorszám"), shown if it fails
  sessionId: string;
  // the machine didn't start; also set by the guide's own start timeout
  failed?: boolean;
  // no running signal within this long → failure screen
  startTimeoutSeconds?: number;
  supportPhone?: string;
};

// long enough to photograph the id or call support from the kiosk
const FAILURE_RETURN_SECONDS = 90;

const TEMPERATURE_TEXTS: StepTexts = {
  title: (
    <Trans>Válassz programot a szárítógép kijelzőjén!</Trans>
  ),
  warning: (
    <Trans>
      Ha a gép már elindult, utána nem tudsz már programot módosítani!
    </Trans>
  ),
  confirmLabel: <Trans>Beállítottam a programot</Trans>,
  audioSlot: "page.dry-start-temperature",
};

const TEXTS = {
  guide: {
    temperature: TEMPERATURE_TEXTS,
    "load-and-close": {
      title: <Trans>Tedd be a ruhákat, és csukd be az ajtót!</Trans>,
      description: (
        <Trans>A gép magától elindul, más gombot nem kell megnyomnod.</Trans>
      ),
    },
  },
  prepare: {
    "load-and-close": {
      title: <Trans>Tedd be a ruhákat, és csukd be az ajtót!</Trans>,
      description: (
        <Trans>
          Itt nincs szükség erős becsapásra, viszont ha nem pattan a helyére az
          ajtó, enyhe emelése segíthet pontosan záródni.
        </Trans>
      ),
      confirmLabel: <Trans>Bent vannak, becsuktam az ajtót!</Trans>,
    },
    temperature: TEMPERATURE_TEXTS,
  },
  "after-prepare": {
    starting: {
      title: <Trans>A gép magától elindul, nincs más dolgod!</Trans>,
      description: (
        <Trans>
          Ha mégsem indul el, nézd meg, hogy jól becsuktad-e az ajtót.
        </Trans>
      ),
    },
  },
} satisfies GuideTexts<typeof DRYER_STEPS, StepTexts>;

const PHASE_STEPS: Record<GuidePhase, GuideStep<DryerView>[]> = {
  guide: stepsWithTexts(DRYER_STEPS.guide, TEXTS.guide),
  prepare: stepsWithTexts(DRYER_STEPS.prepare, TEXTS.prepare),
  "after-prepare": stepsWithTexts(
    DRYER_STEPS["after-prepare"],
    TEXTS["after-prepare"],
  ),
};

export function DryStartGuideScreen(props: DryStartGuideScreenProps) {
  const [timedOut, setTimedOut] = useState(false);
  const failed = guideFailed({
    failed: !!props.failed,
    timedOut,
    isRunning: props.isRunning,
  });
  return (
    <FlowBackSlot>
      <div className="min-h-screen flex flex-col bg-gray-50 text-bub-dark-blue">
        <GuideKeyframes />
        <FlowHeader
          kind="dryer"
          machine={{
            number: props.machineNumber,
            kind: "dryer",
            capacity: props.machineCapacity,
          }}
          {...guideHeader(
            guideStatus(props.phase ?? "guide", props.isRunning, failed),
          )}
        />
        <main className="relative flex-1 flex flex-col px-12 pb-12">
          <DryStartGuideContent
            {...props}
            failed={failed}
            onStartTimeout={() => setTimedOut(true)}
          />
        </main>
      </div>
    </FlowBackSlot>
  );
}

/**
 * The guide without a header, for the payment flow. `failed` is final here:
 * the caller turns `onStartTimeout` into it.
 */
export function DryStartGuideContent({
  machineNumber,
  phase = "guide",
  onPrepared,
  onBack,
  machineCapacity,
  isRunning,
  onTemperatureSet,
  isNewRegistration = false,
  invoice,
  collectorsDiscount,
  currencySymbol = "Ft",
  returnAfterSeconds = 15,
  onStartAnother,
  onFinish,
  sessionId,
  failed = false,
  startTimeoutSeconds = 180,
  onStartTimeout,
  supportPhone = getFeatureFlags().supportPhone,
  onPaidBeatEnd,
}: DryStartGuideScreenProps & {
  onStartTimeout: () => void;
  // the payment flow, right after payment: see `GuideSteps`
  onPaidBeatEnd?: () => void;
}) {
  // the drum spin-up plays in the guide's scene before the success view
  // coming back from the invoice form: straight to the success view
  const [spinUpDone, setSpinUpDone] = useState(!!invoice?.result);
  const view = guideView({ failed, isRunning, spinUpDone });
  const steps = PHASE_STEPS[phase];
  return view === "failed" ? (
    <FailedState
      machineNumber={machineNumber}
      sessionId={sessionId}
      supportPhone={supportPhone}
      returnAfterSeconds={FAILURE_RETURN_SECONDS}
      onFinish={onFinish}
    />
  ) : view === "running" ? (
    <RunningState
      machineNumber={machineNumber}
      wish={<Trans>Jó szárítást kívánunk! Bubbles, a Te mosodád.</Trans>}
      isNewRegistration={isNewRegistration}
      invoice={invoice}
      collectorsDiscount={collectorsDiscount}
      currencySymbol={currencySymbol}
      returnAfterSeconds={returnAfterSeconds}
      onStartAnother={onStartAnother}
      onFinish={onFinish}
    />
  ) : (
    <DryGuideSteps
      steps={steps}
      phase={phase}
      showStartTimeout={showsStartTimeout(phase)}
      showDots={showsStepDots("dryer", phase, steps.length)}
      onLastConfirm={onPrepared}
      onBackFromFirst={onBack}
      machineNumber={machineNumber}
      machineCapacity={machineCapacity}
      isRunning={isRunning}
      onSpinUpEnd={() => setSpinUpDone(true)}
      startTimeoutSeconds={startTimeoutSeconds}
      onStartTimeout={onStartTimeout}
      onPaidBeatEnd={onPaidBeatEnd}
      onTemperatureSet={onTemperatureSet}
    />
  );
}

function DryGuideSteps({
  steps,
  phase,
  machineCapacity,
  isRunning,
  onTemperatureSet,
  ...rest
}: Omit<
  Parameters<typeof GuideSteps<DryerView>>[0],
  "scene" | "onStepConfirm" | "startTimeoutCaption"
> & {
  phase: GuidePhase;
  machineCapacity: number;
  onTemperatureSet?: () => void;
}) {
  return (
    <GuideSteps
      {...rest}
      steps={steps}
      isRunning={isRunning}
      onStepConfirm={(step) => {
        if (sendsStartSignal("dryer", phase, step)) onTemperatureSet?.();
      }}
      startTimeoutCaption={
        <Trans>
          Indítójel elküldve – a szárítógép vár, hogy bezárd az ajtaját
        </Trans>
      }
      scene={(stepIndex) => (
        <DryerScene
          view={isRunning ? "running" : steps[stepIndex]!.view}
          // a door step before this one: the door stays shut
          doorClosed={dryerDoorClosed(steps, stepIndex)}
          capacity={machineCapacity}
        />
      )}
    />
  );
}
